"use client";
import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import Accordion from "@/next-src/components/ui/accordion/Accordion";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";

import comCSS from "./AccordionSection.module.scss";

const AccordionSection = ({ className = "", data }) => {
  const { width: windowWidth } = useWindowDimensions();
  const { bgColorMobile, paddingTopMobile, paddingBottomMobile } = data || {};

  let { bgColor, paddingTop, paddingBottom, items } = data || {};

  if (windowWidth < 992) {
    bgColor = bgColorMobile;
    paddingTop = paddingTopMobile;
    paddingBottom = paddingBottomMobile;
  }
  const getText = (el, keyLG, keyXS) => {
    if (windowWidth < 992) {
      return convertHTMLtoJSX(el?.[keyXS]);
    }
    return convertHTMLtoJSX(el?.[keyLG]);
  };

  const getInnerAccordionData = (accData) =>
    accData
      ?.filter((el) => !el.hide)
      ?.map((el) => ({
        id: el.id,
        header: (
          <>
            <div className="feature-name">{convertHTMLtoJSX(el.title)}</div>
            {el.label ? (
              <div className="small-tag light-grey">
                <div className={comCSS.labelWrapper}>
                  {convertHTMLtoJSX(el.label)}
                </div>
              </div>
            ) : null}
          </>
        ),
        body: (
          <div
            className={cn(
              comCSS.smallParagraph,
              "small-paragraph max-width-medium"
            )}
          >
            {convertHTMLtoJSX(el.text)}
          </div>
        ),
      }));

  const innerAccordion = (accData) => (
    <Accordion
      data={getInnerAccordionData(accData)}
      itemClassName="dark-blue-hover dropdown-cms-item"
      bodyClassName="with-padding"
      headerClassName={cn("is-small", comCSS.innerAccordionHeader)}
      headerLeftClassName="small-gap vertical-on-mobile"
    />
  );

  const getAccordionData = (accData) =>
    accData
      ?.filter((el) => !el.hide)
      ?.map((el) => ({
        id: el.id,
        header: (
          <>
            <div className={cn(comCSS.accordionHeader)}>
              <div className="accordion-icon-wrap">
                <ResponsiveImage
                  width={32}
                  height={32}
                  lazyLoading
                  sizes="32px"
                  alt={el.image?.alt}
                  src={el.image?.src}
                  className="icon-1x1-small"
                />

                <div className="program-name">
                  {getText(el, "title", "titleMobile")}
                </div>
              </div>
              <div className="text-size-small">
                {getText(el, "text", "textMobile")}
              </div>
            </div>
          </>
        ),
        body: innerAccordion(el.children),
      }));
  return (
    <section
      className={cn(
        className,
        comCSS.wrapper,
        "section background-color-soft-blue "
      )}
      style={{
        backgroundColor: bgColor,
        paddingTop:
          paddingTop !== null && paddingTop >= 0 ? `${paddingTop}px` : "",
        paddingBottom:
          paddingBottom !== null && paddingBottom >= 0
            ? `${paddingBottom}px`
            : "",
      }}
    >
      <div className="padding-global">
        <div className="container-large">
          <div className={cn(comCSS.accordionWrapper, "technologies-wrap")}>
            {items
              ?.filter((el) => !el.hide)
              ?.map((el) => (
                <Accordion
                  key={el.id}
                  data={getAccordionData([el])}
                  isReadMore
                  bodyClassName={cn("no-padding ", comCSS.accordionBody)}
                  headerClassName="is-program-name"
                  className="dropdown_accordion type-3"
                  countN={el.children?.filter((el) => !el.hide)?.length}
                  isCloseIconRounded
                  isCloseIconBlack
                  lessText="View Less"
                  moreText="View More"
                  answerMaxHeight={
                    el.children?.filter((el) => !el.hide)?.length * 80
                  }
                />
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

AccordionSection.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired, // Make item ID required for uniqueness
        title: PropTypes.string, // Main title
        titleMobile: PropTypes.string, // Mobile title
        text: PropTypes.string, // Text content
        textMobile: PropTypes.string, // Mobile text content
        image: PropTypes.shape({
          id: PropTypes.string, // Image ID (consider removing if not used)
          src: PropTypes.string, // Make image source required
          alt: PropTypes.string, // Make alt text required for accessibility
        }),
        children: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string, // Child item ID (consider removing if not used)
            title: PropTypes.string, // Child title
            label: PropTypes.string, // Child label
            text: PropTypes.string, // Child text content
          })
        ),
      })
    ),
    paddingTop: PropTypes.number,
    paddingBottom: PropTypes.number,
    paddingTopMobile: PropTypes.number,
    paddingBottomMobile: PropTypes.number,
    bgColor: PropTypes.string, // Background color
    bgColorMobile: PropTypes.string, // Mobile background color
  }),
};

export default AccordionSection;
